import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { CandidateBrowseRow } from '@nest-recruit/shared';
import { Checkbox, isShiftClick } from '../../../../components/Checkbox';
import type { Column } from '../../../../components/data-table/types';
import { Skeleton } from '../../../../components/Skeleton';
import { EMPTY, formatDateTimeLong, formatRelative } from '../../../../lib/format';
import { isSelected, pageCheckboxState, type SelectionState } from '../../../../lib/selection';
import { ACTIVITY_COLUMN } from './candidate-columns';
import { tickPage, tickRow } from './list-selection';
import { RowQuickActions } from './QuickActions';
import type { RowHandlers } from './RowBits';
import styles from './CandidateTable.module.css';

const SKELETON_ROWS = 12;

/** A text field or a control inside the table: its keys are its own, not the row's. */
function isOwnKeyTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'A'].includes(target.tagName));
}

interface CandidateTableProps {
  /** The visible columns, in order; Last activity is drawn by the last cell with the hover actions. */
  readonly columns: readonly Column<CandidateBrowseRow>[];
  readonly rows: readonly CandidateBrowseRow[];
  readonly isLoading: boolean;
  readonly selection: SelectionState;
  readonly onSelectionChange: (selection: SelectionState) => void;
  readonly handlers: RowHandlers;
  readonly emptyState: ReactNode;
}

/**
 * CAND-01's table (mockup Main.dc.html). Built here rather than on the shared DataTable because
 * the mockup's row is taller and richer than DataTable's fixed 40-pixel virtual rows, its last
 * cell trades the time for quick actions on hover, and its selection bar and column chooser sit
 * above the table so the Cards view shares them. It keeps DataTable's behaviours: a sticky header,
 * arrow keys and Enter on rows, Space and shift-click to tick, and `—` for an empty value.
 */
export function CandidateTable({ columns, rows, isLoading, selection, onSelectionChange, handlers, emptyState }: CandidateTableProps): ReactNode {
  const [focused, setFocused] = useState<number | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pageIds = rows.map((row) => row.candidate.id);
  const shown = columns.filter((column) => column.id !== ACTIVITY_COLUMN);
  const isActivityShown = shown.length !== columns.length;
  const pageState = pageCheckboxState(selection, pageIds);

  const focusRow = (index: number): void => {
    const clamped = Math.max(0, Math.min(index, rows.length - 1));
    setFocused(clamped);
    scrollRef.current?.querySelector(`[data-row-index="${clamped}"]`)?.scrollIntoView({ block: 'nearest' });
  };

  // 06-qol §2: arrow keys move, Enter opens the pop-up, Space ticks.
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (rows.length === 0 || isOwnKeyTarget(event.target)) return;
    const current = focused ?? -1;
    const moves: Readonly<Record<string, number>> = { ArrowDown: current + 1, ArrowUp: current - 1, Home: 0, End: rows.length - 1 };
    const target = moves[event.key];
    if (target !== undefined) {
      event.preventDefault();
      focusRow(target);
      return;
    }
    const row = rows[current];
    if (row === undefined) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      handlers.onOpen(row);
    } else if (event.key === ' ') {
      event.preventDefault();
      onSelectionChange(tickRow(selection, row.candidate.id, pageIds, false));
    }
  };

  const showSkeleton = isLoading && rows.length === 0;
  return (
    <>
      <div ref={scrollRef} className={styles.scroll} tabIndex={0} role="region" aria-label="Candidates" onKeyDown={onKeyDown}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col" className={styles.tick}>
                <Checkbox
                  checked={pageState === 'checked'}
                  isIndeterminate={pageState === 'indeterminate'}
                  onChange={(isTicked) => onSelectionChange(tickPage(selection, pageIds, isTicked))}
                  ariaLabel="Select all on this page"
                />
              </th>
              {shown.map((column) => (
                <th key={column.id} scope="col" className={styles[`col-${column.id}`]}>
                  {column.id === 'star' ? <span className="sr-only">{column.header}</span> : column.header}
                </th>
              ))}
              <th scope="col" className={styles['col-activity']}>
                {isActivityShown ? 'Last activity' : <span className="sr-only">Actions</span>}
              </th>
            </tr>
          </thead>
          <tbody>
            {showSkeleton &&
              Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <tr key={`skeleton-${index}`} className={styles.row} aria-hidden>
                  <td className={styles.tick} />
                  {shown.map((column) => (
                    <td key={column.id}>
                      <Skeleton width={column.isIdentity ? '80%' : '60%'} />
                    </td>
                  ))}
                  <td>
                    <Skeleton width="60%" />
                  </td>
                </tr>
              ))}
            {rows.map((row, index) => {
              const id = row.candidate.id;
              const isTicked = isSelected(selection, id);
              return (
                <tr
                  key={id}
                  data-row-index={index}
                  className={styles.row}
                  data-selected={isTicked || undefined}
                  data-focused={focused === index || undefined}
                  aria-selected={isTicked}
                  onClick={() => setFocused(index)}
                >
                  <td className={styles.tick}>
                    <Checkbox checked={isTicked} onChange={(_checked, event) => onSelectionChange(tickRow(selection, id, pageIds, isShiftClick(event)))} ariaLabel={`Select ${row.candidate.fullName}`} />
                  </td>
                  {shown.map((column) => {
                    const content = column.cell(row);
                    const isEmpty = content === null || content === undefined || content === '';
                    return (
                      <td key={column.id} className={isEmpty ? styles.empty : undefined}>
                        {isEmpty ? EMPTY : content}
                      </td>
                    );
                  })}
                  <td className={styles.last}>
                    {isActivityShown && (
                      <span className={styles.when} title={row.lastActivityAt === null ? undefined : formatDateTimeLong(row.lastActivityAt)}>
                        {row.lastActivityAt === null ? EMPTY : formatRelative(row.lastActivityAt)}
                      </span>
                    )}
                    <span className={styles.quick}>
                      <RowQuickActions row={row} handlers={handlers} />
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!isLoading && rows.length === 0 && emptyState}
    </>
  );
}
